import { useCallback, useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { QUERY_KEYS } from '@restworld/utils-common';
import { Worker } from '@restworld/utility-types';

import { setWorkerAtom } from '../components/global-state.worker-data';

export const useFetchWorkerDetailsQueryKey = () => {
  const { user } = useAuth();
  return [QUERY_KEYS.WORKERS.FETCH_WORKER_DETAILS, user?.id];
};
export const useFetchWorkerDataQuery = () => {
  const workerService = useWorkersService();
  const { user } = useAuth();
  const onSetWorkerData = useSetAtom(setWorkerAtom);
  const { workerId, email, phoneNumber, userType } = useMemo(
    () => ({
      workerId: user?.id,
      email: user?.email,
      phoneNumber: user?.phone_number,
      userType: user?.type,
    }),
    [user]
  );
  const queryKey = useFetchWorkerDetailsQueryKey();

  const queryFn = useCallback(async () => {
    const fetchWorkerData = (await workerService.fetchWorker(workerId))?.data;
    const combinedWorkerData = {
      ...fetchWorkerData,
      user: { ...fetchWorkerData.user, email: email, phone_number: phoneNumber },
    } as Worker;
    onSetWorkerData(combinedWorkerData);
    return combinedWorkerData;
  }, [email, onSetWorkerData, phoneNumber, workerId, workerService]);

  const fetchData = useQuery({
    queryKey,
    queryFn,
    enabled: !!workerId && userType === 'worker',
  });

  return fetchData;
};
export const useFetchWorkerDataLoading = () =>
  useQueryClient().getQueryState(useFetchWorkerDetailsQueryKey())?.status === 'pending';

export const useFetchWorkerData = () => {
  const queryClient = useQueryClient();
  const queryKey = useFetchWorkerDetailsQueryKey();
  return queryClient.getQueryData(queryKey) as Worker;
};
